import { bumpCartEpoch, emptyGuestCart } from './cartRecovery.js'
import { removeLegacyCart } from './legacyCartImport.js'
import { getSessionApi } from './sessionApi.js'

const CLEARED_AUTH_COOKIE = 'Authorization=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'

function storageOf(storage) {
	if (storage) return storage
	if (typeof window !== 'undefined' && window.localStorage) return window.localStorage
	return null
}

function clearKeys(storage, keys) {
	if (!storage?.removeItem) return
	for (const key of keys) {
		try {
			storage.removeItem(key)
		} catch {
			// storage can throw in private mode
		}
	}
}

/**
 * Browser logout. Drops the session and the page's in-memory cart.
 * Does not read, write, or delete the member cart document.
 */
export async function logoutSession({
	store,
	axios,
	checkout,
	storage,
	writeCookie,
	clearMangoAuthorization = () => {},
	api,
} = {}) {
	const localStorage = storageOf(storage)

	// Tell the API while the bearer token and the cookie are still present, so it can
	// destroy the Redis session and revoke that token. A network failure still clears
	// this browser; the other devices are signed out on password reset.
	const logoutApi = api || getSessionApi()
	if (logoutApi && typeof axios?.post === 'function') {
		let token = ''
		try {
			token = localStorage?.getItem('token') || ''
		} catch {
			token = ''
		}
		try {
			await axios.post(`${logoutApi}/controllers/account/logout`, {}, {
				withCredentials: true,
				headers: token ? { Authorization: token } : {},
			})
		} catch {
			// local logout still proceeds
		}
	}

	// Abandon saves still bound to the logged-in cart module before the token is gone,
	// so they cannot sync that cart as a guest or write it back.
	bumpCartEpoch()

	clearKeys(localStorage, ['user', 'token', 'email', 'auth'])
	// The cart is on the server; this only drops a leftover pre-rollout browser copy.
	removeLegacyCart(localStorage)

	const write = writeCookie || ((value) => {
		if (typeof document !== 'undefined') document.cookie = value
	})
	write(CLEARED_AUTH_COOKIE)

	if (axios?.defaults?.headers?.common) {
		delete axios.defaults.headers.common['Authorization']
		delete axios.defaults.headers.common['authorization']
	}
	try {
		clearMangoAuthorization()
	} catch {
		// clearing the client header cache must not block logout
	}

	if (store) store.user = {}
	if (typeof window !== 'undefined') window.user = null

	// Replace the cart object. Mutating the logged-in cart in place would
	// notify its watcher and save an empty cart onto the member document.
	const empty = emptyGuestCart()
	if (store) store.cart = empty

	if (checkout?.resetCheckoutForLogout) checkout.resetCheckoutForLogout()

	if (typeof store?.initUserCart === 'function') {
		try {
			await store.initUserCart()
		} catch {
			store.cart = emptyGuestCart()
		}
	}

	return { cart: store?.cart || empty }
}
